// Capitalize first letter in string
import urlize from './urlize';

export { urlize };
export function capitalize<S extends string>(word: S): Capitalize<S> {
	return word.charAt(0).toUpperCase() + word.slice(1) as Capitalize<S>;
};

export function parseDate(date: number | string | Date) {
	const result = new Date(date);
	// ensure date-only strings are parsed as local dates
	// see https://maggiepint.com/2017/04/11/fixing-javascript-date-web-compatibility-and-reality/
	if (typeof date === 'string' && !date.includes('T')) {
		return new Date(result.getTime() + result.getTimezoneOffset() * 60000);
	}
	return result;
}

const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
export function getDate(date: number | string | Date) {
	const d = parseDate(date);
	return `${months[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
};

export function isPrerender() {
	return typeof window === 'undefined';
}

export function isWithinFrame() {
	if (isPrerender()) return false;
	// https://stackoverflow.com/questions/326069/how-to-identify-if-a-webpage-is-being-loaded-inside-an-iframe-or-directly-into-t/326076#326076
	try {
		return window.self !== window.top;
	}
	catch (e) {
		return true;
	}
}

/***
 * Returns the closest scrollable ancestor
 * found at https://stackoverflow.com/questions/35939886/find-first-scrollable-parent#42543908
 */
export function getScrollParent(element: Element, includeHidden: boolean) {
	let style = getComputedStyle(element);
	const excludeStaticParent = style.position === 'absolute';
	const overflowRegex = includeHidden ? /(auto|scroll|hidden)/ : /(auto|scroll)/;

	if (style.position === 'fixed') return document.body;
	for (let parent : Element | HTMLElement | null = element; (parent = parent.parentElement);) {
		style = getComputedStyle(parent);
		if (excludeStaticParent && style.position === 'static') {
			continue;
		}
		if (overflowRegex.test(style.overflow + style.overflowY + style.overflowX)) {
			return parent;
		}
	}

	return document.body;
}

export function slugify(s: string) {
	// Ensure the slugification of headers and TOC links is the same. Both use this function.
	// https://github.com/valeriangalliat/markdown-it-anchor/blob/e276fe53e259bcd2bf6045b6093f82d3cd606f8c/index.js#L1
	// Also remove special chars, some html entities, and repeating/trailing dashes
	return encodeURIComponent(
		String(s)
			.trim()
			.toLowerCase()
			.replace(/&shy;/g, '-')
			.replace(/&nbsp;/g, '-')
			.replace(/&ndash;/g, '-')
			.replace(/&mdash;/g, '-')
			.replace(/\s+/g, '-')
			.replace(/\//g, '')
			.replace(/[^a-zA-Z0-9]/g, '-')
			.replace(/-+/g, '-')
			.replace(/-$/g, '')
	);
}

export function getAbbreviatedNumber(num: number) {
	if (num > 99999) {
		// Round to 1 decimal
		let numString = (num / 1000).toFixed(1);

		// Only show decimal if needed
		if (numString.endsWith('.0'))
			numString = numString.split('.')[0]!;

		return numString + 'k';
	}
	return num;
};

// https://stackoverflow.com/questions/123999/how-can-i-tell-if-a-dom-element-is-visible-in-the-current-viewport/7557433#7557433
/**
 * Checks to see if an element is in the viewport, this should be used in a requestAnimationFrame
 *
 * @param el element to check
 * @returns wether the element is in the viewport.
 */
export function isElementInViewport(el: HTMLElement) {
	const rect = el.getBoundingClientRect();

	return (
		rect.top >= 0 &&
        rect.left >= 0 &&
        rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
        rect.right <= (window.innerWidth || document.documentElement.clientWidth)
	);
}